// The small set of plain components every page is drawn with
// (01-RESEARCH/004-console-experience/06-the-look.md § components): a button
// in four kinds, a field, the scope filter, a card, a tile, a table with a
// sunken header row, a pill and a dot, a one-time value with its copy button,
// a list row, the empty state, the menu and the confirmation. Each is
// plain CSS on the tokens, and nothing here knows the wire.

import { Check, Copy } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";

/**
 * CODE_WORDS are refusals of a guarded call that name a step rather than a
 * person, said plainly where a refusal carries its code and no message of its
 * own: a transformer a guardrail hands the call to, and a model's content
 * callout (01-rules-and-enforcement.md § the outcome on the wire;
 * 02-the-inference-contract.md § the content callout). The service's own
 * message, which names the step, is shown when there is one.
 */
export const CODE_WORDS: Readonly<Record<string, string>> = {
  "guard-transformer-unavailable": "the transformer did not answer",
  "guard-transformer-failed": "the transformer could not transform",
  "callout-unavailable": "what inspects every request to the model did not answer",
};

/** describe renders an error for a person: its message, or the words of its code where the message is only the code. */
export function describe(err: unknown): string {
  if (err instanceof Error) {
    const code = "code" in err && typeof err.code === "string" ? err.code : undefined;
    if (code !== undefined && (err.message === "" || err.message === code)) {
      return CODE_WORDS[code] ?? err.message;
    }
    return err.message;
  }
  return String(err);
}

/** Tone colours a pill or a dot: a signal, never decoration. */
export type Tone = "neutral" | "live" | "ok" | "bad" | "info";

export function Notice({
  kind = "info",
  title,
  children,
}: {
  kind?: "info" | "bad" | "ok";
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`notice ${kind}`} role={kind === "bad" ? "alert" : "status"}>
      {title !== undefined && <div className="title">{title}</div>}
      {children !== undefined && <div className="text">{children}</div>}
    </div>
  );
}

/**
 * NOT_ALLOWED are the codes that refuse who is asking: the chain's identity
 * refusals, a verified caller the service will not act for, a request the
 * broker did not admit for the role, a guardrail's denial, a session gone.
 */
const NOT_ALLOWED = new Set([
  "identity-required",
  "identity-invalid",
  "unauthorized",
  "not-admitted",
  "guard-denied",
  "signed-out",
]);

/**
 * refusedTitle names what happened to a request: *Not allowed* when the code
 * refuses who is asking, *failed* for anything else — a conflict, a budget, a
 * service that could not do it (§ the words; hq issue 078). An error that
 * carries no code is a failure.
 */
export function refusedTitle(err: unknown, what?: string): string {
  const code = typeof err === "object" && err !== null && "code" in err ? err.code : undefined;
  const allowed = typeof code !== "string" || !NOT_ALLOWED.has(code);
  if (what === undefined) {
    return allowed ? "Failed" : "Not allowed";
  }
  return allowed ? `${what}: failed` : `${what}: not allowed`;
}

/**
 * NotAllowed shows a refused or failed request as what it is — *Not allowed*
 * for a refusal of who is asking, *failed* for the rest — with the reason the
 * service gave (§ the words), never as a hang or a guess.
 */
export function NotAllowed({ err, what }: { err: unknown; what?: string }) {
  return (
    <Notice kind="bad" title={refusedTitle(err, what)}>
      {describe(err)}
    </Notice>
  );
}

/** Empty is the empty state: one sentence and the page's primary action. */
export function Empty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <p>{children}</p>
      {action}
    </div>
  );
}

export function Pending({ what }: { what: string }) {
  return (
    <p className="faint" aria-busy="true">
      {what}…
    </p>
  );
}

/** PageHead is every page's first line: the title, one line under it, the one primary action on the right. */
export function PageHead({
  title,
  line,
  action,
  crumb,
}: {
  title: ReactNode;
  line?: ReactNode;
  action?: ReactNode;
  crumb?: ReactNode;
}) {
  return (
    <>
      {crumb !== undefined && <div className="crumb">{crumb}</div>}
      <header className="head">
        <div>
          <h1>{title}</h1>
          {line !== undefined && <p className="sub">{line}</p>}
        </div>
        {action !== undefined && <div className="head-actions">{action}</div>}
      </header>
    </>
  );
}

/** Scope is one list's two scopes: a person's own, or everyone's. */
export type Scope = "yours" | "everyone";

/**
 * ScopeFilter is the one filter on a list a design gives a member their own of
 * and an admin everyone's (§ the shape): shown only to a role offered the
 * wider scope, defaulting to *Yours*.
 */
export function ScopeFilter({
  value,
  onChange,
  counts,
}: {
  value: Scope;
  onChange: (s: Scope) => void;
  counts?: { yours: number; everyone: number };
}) {
  const tab = (s: Scope, label: string, count: number | undefined) => (
    <button
      type="button"
      role="tab"
      className="tab"
      aria-selected={value === s}
      onClick={() => {
        onChange(s);
      }}
    >
      {label}
      {count !== undefined && <span className="count">{count}</span>}
    </button>
  );
  return (
    <div className="tabs" role="tablist" aria-label="Whose">
      {tab("yours", "Yours", counts?.yours)}
      {tab("everyone", "Everyone's", counts?.everyone)}
    </div>
  );
}

/** Tile is one thing the role may read, at a glance. */
export function Tile({
  icon,
  label,
  value,
  note,
  to,
  attention,
}: {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  note?: ReactNode;
  to?: string;
  attention?: boolean;
}) {
  const body = (
    <>
      <div className="tile-label">
        {icon}
        {label}
      </div>
      <div className="tile-value">{value}</div>
      {note !== undefined && <div className="tile-note">{note}</div>}
    </>
  );
  const cls = attention === true ? "tile attention" : "tile";
  if (to !== undefined) {
    return (
      <Link className={cls} to={to}>
        {body}
      </Link>
    );
  }
  return <div className={cls}>{body}</div>;
}

export function Pill({
  tone = "neutral",
  strong,
  children,
}: {
  tone?: Tone;
  strong?: boolean;
  children: ReactNode;
}) {
  return <span className={`pill ${tone}${strong === true ? " strong" : ""}`}>{children}</span>;
}

export function Dot({ tone = "neutral" }: { tone?: Tone }) {
  return <span className={`dot ${tone}`} aria-hidden="true" />;
}

/** Avatar is the one letter that stands for a person. */
export function Avatar({ name, small, square }: { name: string; small?: boolean; square?: boolean }) {
  const first = name.trim().charAt(0);
  const cls = ["avatar", small === true ? "sm" : "", square === true ? "square" : ""]
    .filter((c) => c !== "")
    .join(" ");
  return (
    <span className={cls} aria-hidden="true">
      {first === "" ? "?" : first.toLocaleUpperCase()}
    </span>
  );
}

export function Button({
  kind = "default",
  size,
  block,
  onClick,
  disabled,
  type = "button",
  label,
  children,
}: {
  kind?: "default" | "primary" | "quiet" | "danger" | "danger-quiet" | "icon";
  size?: "sm" | "lg";
  block?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  /** The accessible name of an icon-only button. */
  label?: string;
  children: ReactNode;
}) {
  const cls = [
    "btn",
    kind === "default" ? "" : kind === "danger-quiet" ? "danger quiet" : kind,
    size ?? "",
    block === true ? "block" : "",
  ]
    .filter((c) => c !== "")
    .join(" ");
  return (
    <button className={cls} type={type} onClick={onClick} disabled={disabled} aria-label={label}>
      {children}
    </button>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {hint !== undefined && <span className="hint">{hint}</span>}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  mono,
  type = "text",
  required,
  autoFocus,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  type?: "text" | "password" | "number" | "url";
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  return (
    <input
      className={mono === true ? "input mono" : "input"}
      type={type}
      value={value}
      placeholder={placeholder}
      required={required}
      autoFocus={autoFocus}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      autoComplete="off"
      spellCheck={false}
    />
  );
}

/** TextArea is a text field of several lines: settings as text, a type's record, a change's data. */
export function TextArea({
  value,
  onChange,
  rows = 10,
  mono,
  placeholder,
  disabled,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  mono?: boolean;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}) {
  return (
    <textarea
      className={mono === true ? "input mono" : "input"}
      rows={rows}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      autoComplete="off"
      spellCheck={false}
    />
  );
}

/** Select is one choice among a few, where a radio group would take too much room. */
export function Select<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      className="input"
      value={value}
      disabled={disabled}
      onChange={(e) => {
        onChange(e.target.value as T);
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Choices is a radio group where each option says what it means in one line. */
export function Choices<T extends string>({
  name,
  value,
  onChange,
  options,
}: {
  name: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; hint?: string; disabled?: boolean }[];
}) {
  return (
    <div className="choices" role="radiogroup">
      {options.map((o) => (
        <label key={o.value}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            disabled={o.disabled}
            onChange={() => {
              onChange(o.value);
            }}
          />
          <span className="what">
            <span>{o.label}</span>
            {o.hint !== undefined && <span className="hint">{o.hint}</span>}
          </span>
        </label>
      ))}
    </div>
  );
}

/**
 * OnceValue shows a value the platform will never show again — an app's key —
 * with its copy button.
 */
export function OnceValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return;
    }
    const t = setTimeout(() => {
      setCopied(false);
    }, 2000);
    return () => {
      clearTimeout(t);
    };
  }, [copied]);
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="once">
        <span className="grow">{value}</span>
        <Button
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? <Check size={14} strokeWidth={1.75} /> : <Copy size={14} strokeWidth={1.5} />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Confirm offers an act that cannot be undone twice, the second time with its
 * consequence in the person's words (§ the shape).
 */
export function Confirm({
  label,
  consequence,
  onConfirm,
  kind = "danger",
  disabled,
}: {
  label: string;
  consequence: string;
  onConfirm: () => void;
  kind?: "danger" | "default";
  disabled?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <Button
        kind={kind === "danger" ? "danger-quiet" : "quiet"}
        size="sm"
        disabled={disabled}
        onClick={() => {
          setArmed(true);
        }}
      >
        {label}
      </Button>
    );
  }
  return (
    <span className="confirm" role="group">
      <span className="why">{consequence}</span>
      <Button
        kind={kind}
        size="sm"
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {label}
      </Button>
      <Button
        kind="quiet"
        size="sm"
        onClick={() => {
          setArmed(false);
        }}
      >
        Keep
      </Button>
    </span>
  );
}

const sameYear = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});
const otherYear = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** when renders an RFC 3339 instant for a person; the zero time is "never". */
export function when(iso: string | undefined): string {
  if (iso === undefined || iso === "" || iso.startsWith("0001-01-01")) {
    return "never";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return (d.getFullYear() === new Date().getFullYear() ? sameYear : otherYear).format(d);
}

/** since renders how long ago an instant was, in a person's words. */
export function since(iso: string | undefined, now = Date.now()): string {
  if (iso === undefined || iso === "" || iso.startsWith("0001-01-01")) {
    return "";
  }
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) {
    return "";
  }
  const s = Math.floor(ms / 1000);
  if (s < 45) {
    return "just now";
  }
  const m = Math.floor(s / 60);
  if (m < 2) {
    return "a minute ago";
  }
  if (m < 60) {
    return `${m} minutes ago`;
  }
  const h = Math.floor(m / 60);
  if (h < 2) {
    return "an hour ago";
  }
  if (h < 24) {
    return `${h} hours ago`;
  }
  const d = Math.floor(h / 24);
  if (d < 2) {
    return "yesterday";
  }
  if (d < 14) {
    return `${d} days ago`;
  }
  const w = Math.floor(d / 7);
  if (w < 9) {
    return `${w} weeks ago`;
  }
  return when(iso);
}

/** bytes renders a byte count. */
export function bytes(n: number): string {
  if (n < 1024) {
    return `${n} B`;
  }
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i] ?? ""}`;
}

/** plural renders a count with its noun. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Load is the state of something being read: pending until the first answer, then the value or the refusal. */
export type Load<T> =
  { state: "pending" } | { state: "ready"; value: T } | { state: "refused"; err: unknown };

/**
 * useLoad reads something keyed by `key`; the read runs again when the key
 * changes or `tick` is bumped. No state is set synchronously in the effect.
 * With `keep`, a read again under the same key shows the last answer until
 * the new one arrives rather than pending: what a page that polls wants.
 */
export function useLoad<T>(
  key: string,
  read: () => Promise<T>,
  tick = 0,
  { keep = false }: { keep?: boolean } = {},
): [Load<T>, () => void] {
  const [state, setState] = useState<{ key: string; tick: number; load: Load<T> }>({
    key: "",
    tick: -1,
    load: { state: "pending" },
  });
  const [bump, setBump] = useState(0);
  const want = tick + bump;
  useEffect(() => {
    let live = true;
    read().then(
      (value) => {
        if (live) {
          setState({ key, tick: want, load: { state: "ready", value } });
        }
      },
      (err: unknown) => {
        if (live) {
          setState({ key, tick: want, load: { state: "refused", err } });
        }
      },
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `read` is a fresh closure each render; the key and tick say when to read again
  }, [key, want]);
  const current: Load<T> =
    state.key === key && (state.tick === want || keep) ? state.load : { state: "pending" };
  return [
    current,
    () => {
      setBump((b) => b + 1);
    },
  ];
}
